import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CustomAccordion from "../../../../../components/display/CustomAccordion";
import UserHint from "../../../../../components/display/UserHint";
import ErrorFullScreenMessage from "../../../../../components/fullScreenMessage/ErrorFullScreenMessage";
import Loader from "../../../../../components/loader/Loader";
import PageContentWrapper from "../../../../../components/wrappers/PageContentWrapper";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import { SnackbarContext } from "../../../../../contexts/Snackbar";
import { CLINICS, CLINICS_EDITING } from "../../../../../enums/ApiPaths";
import { EDIT_CLINIC } from "../../../../../enums/PermissionTitles";
import useUser from "../../../../../hooks/useUser";
import { snackbarSeverity } from "../../../../../hooks/useSnackbar";
import logger from "../../../../../utils/logger";
import { CharLimit } from "../../../../feedback/FeedbackPage";
import { useFetchClinicData } from "../../../../master/clinics/utils/DataFetching";
import usePutClinic from "../../hooks/usePutClinic";
import { useRedirectIfNotOwnClinic } from "../../../../../hooks/useRedirectIfNotOwnClinic";
import WarehouseCodePrefixSettings from "./WarehouseCodePrefixSettings";
import WarehouseCodeConflictsDialog from "./WarehouseCodeConflictsDialog";
import WarehousePricingSettings from "./WarehousePricingSettings";
import InvoiceSettings from "./InvoiceSettings";
import { useWarehouseCodePrefixField } from "../warehouse/hooks/useWarehouseCodePrefixField";
import { useWarehousePreferencesField } from "../warehouse/hooks/useWarehousePreferencesField";
import { useInvoiceSettingsField } from "./hooks/useInvoiceSettingsField";
const marginRight = 5;
const clinicDescription = "clinic.description"
const maxPendingLimit = "clinic.maxPendingAppointments"
export default function ClinicOptions() {
  const { id } = useParams();
  useRedirectIfNotOwnClinic(id);
  const { data, isLoading, error } = useFetchClinicData(id);
  const { t } = useTranslation();
  const methods = useForm();

  const clinic = data?.clinic || {};
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const clinicEditing = usePutClinic(CLINICS_EDITING, CLINICS + id);
  const hasPermission = isPermissionAllowed(EDIT_CLINIC);
  const { roles } = useUser();
  const navigate = useNavigate();
  const warehousePrefixField = useWarehouseCodePrefixField(id);
  const {
    ConfirmationDialog: WarehousePrefixConfirmationDialog,
  } = warehousePrefixField;
  const warehousePreferencesField = useWarehousePreferencesField(id);
  const invoiceSettingsField = useInvoiceSettingsField(id);

  React.useEffect(() => {
    if (clinic) {
      Object.keys(clinic).forEach((key) => {
        if (clinic[key] !== undefined && clinic[key] !== null) {
          methods.setValue(`clinic.${key}`, String(clinic[key]));
        }
      });
    }
  }, [clinic, methods.setValue]);

  async function submitData(data) {
    const { clinic: updatedClinic } = data;
    delete updatedClinic.workingDaysList;
    delete updatedClinic.informationFields;

    if (hasPermission) {
      try {
        await clinicEditing.submitCallback(updatedClinic, true);
        await warehousePrefixField.save();
        await warehousePreferencesField.save();
        await invoiceSettingsField.save();
      } catch (error) {
        logger.error("Saving clinic options failed:", error);
        handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
      }
    }
  }

  // Without this, a failed client-side validation just silently skips the
  // submit and the user is left waiting for a response that never comes.
  function handleInvalid() {
    handleSnackbar("FormError", snackbarSeverity.ERROR);
  }

  if (isLoading || !hasPermission) {
    return <Loader />;
  } else if (error) {
    return (
      <PageContentWrapper>
        <ErrorFullScreenMessage />
      </PageContentWrapper>
    );
  } else if (!isLoading) {
    if (!hasPermission) {
      navigate(-1);
      return;
    }
  }

  const appointmentSettings = {
    title: "AppointmentSettings",
    component: (
      <Controller
        name={maxPendingLimit}
        control={methods.control}
        render={({ field }) => (
          <TextField
            fullWidth
            {...field}
            value={field.value ?? 1}
            InputProps={{ inputProps: { min: 1, "data-testid": "clinic.options.max-pending-input" } }}
            type="number"
            label={t("maxPendingAppointments")}
            error={
              !!methods.formState.errors?.clinic?.["maxPendingAppointments"]
            }
            helperText={
              methods.formState.errors?.clinic?.["maxPendingAppointments"]
                ?.message || ""
            }
            sx={{
              mr: marginRight,
            }}
          />
        )}
      />
    ),
    showTitle: false,
    info: <UserHint iconOnly hint={"MaximumPendingAppointmentsUserHint"} />,
  };

  const aboutUsSettings = {
    title: "AboutUsInformation",
    component: (
      <Stack
        width={"100%"}
        sx={{
          mr: marginRight,
        }}
      >
        <Controller
          name={clinicDescription}
          control={methods.control}
          render={({ field }) => (
            <TextField
              fullWidth
              multiline
              rows={8}
              {...field}
              value={field.value ?? ""}
              inputProps={{ "data-testid": "clinic.options.description-input" }}
              label={t("Description")}
              placeholder={t("Description")}
              onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 5000) {
                    methods.setValue(clinicDescription, newValue)
                  }
                }}
            />
          )}
        />
        <CharLimit
          string={methods.watch(clinicDescription)}
          maxLeng={5000}
        />
      </Stack>
    ),
    showTitle: true,
    info: <UserHint iconOnly hint={"ClinicAdditionalInformationPlaceholder"} />,
  };

  const viewClinic = {
    title: "MasterSettings",
    component: (
      <Stack
        border={"1px solid gray"}
        p={1}
        borderRadius={"5px"}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography>{t("HideClinic")}</Typography>

        <Controller
          name={"clinic.hidden"}
          control={methods.control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  inputProps={{ "data-testid": "clinic.options.hidden-switch" }}
                  defaultChecked={Boolean(clinic?.hidden) === true}
                  {...field}
                />
              }
            />
          )}
        />
      </Stack>
    ),
    showTitle: false,
  };

  const aiAssistantSettings = {
    title: "EnableAiAssistant",
    component: (
      <Stack
        border={"1px solid gray"}
        p={1}
        borderRadius={"5px"}
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography>{t("EnableAiAssistant")}</Typography>

        <Controller
          name={"clinic.aiAssistantEnabled"}
          control={methods.control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  inputProps={{ "data-testid": "clinic.options.ai-assistant-switch" }}
                  defaultChecked={Boolean(clinic?.aiAssistantEnabled) === true}
                  {...field}
                />
              }
            />
          )}
        />
      </Stack>
    ),
    showTitle: false,
  };

  const warehouseCodePrefixSettings = {
    title: "CodePrefix",
    component: (
      <WarehouseCodePrefixSettings
        prefix={warehousePrefixField.prefix}
        onChange={warehousePrefixField.setPrefix}
      />
    ),
    showTitle: false,
  };

  const warehousePricingSettings = {
    title: "PricingSettings",
    component: (
      <WarehousePricingSettings
        priceEntryMode={warehousePreferencesField.priceEntryMode}
        onPriceEntryModeChange={warehousePreferencesField.setPriceEntryMode}
        defaultVat={warehousePreferencesField.defaultVat}
        onDefaultVatChange={warehousePreferencesField.setDefaultVat}
        currency={warehousePreferencesField.currency}
        onCurrencyChange={warehousePreferencesField.setCurrency}
      />
    ),
    showTitle: true,
  };

  const invoiceSettings = {
    title: "InvoiceSettingsFields",
    component: (
      <InvoiceSettings
        scheme={invoiceSettingsField.scheme}
        onSchemeChange={invoiceSettingsField.setScheme}
        premisesCode={invoiceSettingsField.premisesCode}
        onPremisesCodeChange={invoiceSettingsField.setPremisesCode}
        nextInvoiceNumber={invoiceSettingsField.nextInvoiceNumber}
        onNextInvoiceNumberChange={invoiceSettingsField.setNextInvoiceNumber}
        onSaveStartingSequence={invoiceSettingsField.saveStartingSequence}
        isUpdatingSequence={invoiceSettingsField.isUpdatingSequence}
        ConfirmationDialog={invoiceSettingsField.ConfirmationDialog}
      />
    ),
    showTitle: false,
  };

  const clinicSettings = [
    {
      title: "ClinicSettings",
      testId: "clinic.options.clinic-settings",
      items: [appointmentSettings, aboutUsSettings],
      expanded: false,
    },
    {
      title: "WarehouseSettings",
      testId: "clinic.options.warehouse-settings",
      items: [warehouseCodePrefixSettings, warehousePricingSettings],
      expanded: false,
    },
    {
      title: "InvoiceSettings",
      testId: "clinic.options.invoice-settings",
      items: [invoiceSettings],
      expanded: false,
    },
  ];

  const master = [
    {
      title: "MasterSettings",
      testId: "clinic.options.master-settings",
      // Basic tier clinics have no access to the AI assistant
      items: [viewClinic, ...(clinic?.basicTier ? [] : [aiAssistantSettings])],
      expanded: false,
    },
  ];

  const forms = [...clinicSettings, ...(roles?.master ? master : [])];

  return (
    <PageContentWrapper>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submitData, handleInvalid)}>
          {forms.map((group, groupIndex) => (
            <CustomAccordion
              key={groupIndex}
              title={group.title}
              testId={group.testId}
              defaultExpanded={group.expanded}
            >
              <Stack spacing={2}>
                {group.items.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    <Stack spacing={1}>
                      {item.showTitle ? (
                        <Typography
                          variant="body2"
                          color={item.textColor || "text.secondary"}
                        >
                          {t(item.title)}
                        </Typography>
                      ) : null}
                      {item?.info ? (
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                          alignItems={"center"}
                        >
                          {item.component}
                          {item?.info}
                        </Stack>
                      ) : (
                        item.component
                      )}
                    </Stack>
                    {itemIndex < group.items.length - 1 && (
                      <Divider sx={{ my: 1 }} />
                    )}
                  </React.Fragment>
                ))}
              </Stack>
            </CustomAccordion>
          ))}
          <Box display="flex" justifyContent="flex-start" mt={2}>
            <Button
              disabled={
                !hasPermission ||
                clinicEditing.loading ||
                warehousePrefixField.isUpdating ||
                warehousePreferencesField.isUpdating ||
                invoiceSettingsField.isUpdating
              }
              variant="contained"
              type="submit"
              data-testid="clinic.options.save-button"
            >
              {t("Save")}
            </Button>
          </Box>
        </form>
      </FormProvider>
      <WarehousePrefixConfirmationDialog />
      <WarehouseCodeConflictsDialog
        conflicts={warehousePrefixField.conflicts}
        onClose={warehousePrefixField.clearConflicts}
      />
    </PageContentWrapper>
  );
}
